import Handsontable from "handsontable";
import type { TableRow } from "./types";

// Render customizado para mostrar error e disable
export function createTextErrorRenderer() {
  return function textErrorRenderer(
    this: any,
    instance: any,
    td: any,
    row: any,
    col: any,
    prop: any,
    value: any,
    cellProperties: any,
  ) {
    // Renderiza o texto base usando o renderer padrão do Handsontable
    Handsontable.renderers.TextRenderer.call(
      this,
      instance,
      td,
      row,
      col,
      prop,
      value,
      cellProperties,
    );

    const rowData = instance.getSourceDataAtRow(row) as TableRow;

    // Verifica se a célula deve ficar desabilitada (título só é editável para Host)
    const isDisabled = prop === "title" && rowData?.type !== "Host";

    if (isDisabled) {
      td.classList.add("disabled-cell");
    } else {
      td.classList.remove("disabled-cell");
    }

    // Aplica indicação visual de erro (borda + ícone)
    const error = rowData?._errors?.[prop];
    const existingIcon = td.querySelector(".cell-error-icon");
    if (existingIcon) existingIcon.remove();

    td.style.whiteSpace = "nowrap";
    td.style.overflow = "hidden";
    td.style.textOverflow = "ellipsis";

    if (error && !isDisabled) {
      td.classList.add("htInvalid");
      td.title = error;
      td.style.position = "relative";
      const errIcon = document.createElement("span");
      errIcon.className = "cell-error-icon";
      errIcon.textContent = "!";
      td.appendChild(errIcon);
    } else {
      td.classList.remove("htInvalid");
      td.title = isDisabled
        ? "Disponível apenas para o tipo Host"
        : value || "";
    }
  };
}

function processSvgImage(imgData: string, color: string, size: string): string {
  if (!imgData?.includes("svg")) return imgData;
  try {
    const svgString = atob(imgData.split("base64,")[1]);
    const container = document.createElement("div");
    container.innerHTML = svgString;
    const svgElement = container.querySelector("svg");
    if (svgElement) {
      svgElement.style.fill = color;
      svgElement.style.width = size;
      svgElement.style.height = "auto";
      return "data:image/svg+xml;base64," + btoa(container.innerHTML);
    }
  } catch {}
  return imgData;
}

/**
 * Pré-processa todos os ícones uma única vez em um Map (nome → data URL pronto).
 * Evita rodar atob/btoa + parse de SVG a cada render de célula, que era a
 * principal causa de travamento ao colar muitas linhas.
 */
export function buildIconImageCache(
  iconsElement: any[],
  iconsColor: string,
): Map<string, string> {
  const cache = new Map<string, string>();
  (iconsElement || []).forEach((icon: any) => {
    if (icon?.nome && icon?.img) {
      cache.set(icon.nome, processSvgImage(icon.img, iconsColor, "20px"));
    }
  });
  return cache;
}

// Render das tags como "chips" leves (DOM puro + classes CSS). Sem atob/SVG,
// para manter a performance ao rolar/colar muitas linhas. A mutação acontece
// pelo popover (a coluna usa editor: false).
export function createTagsRenderer() {
  const MAX_VISIBLE = 3;
  return function tagsRenderer(
    _instance: any,
    td: any,
    _row: any,
    _col: any,
    _prop: any,
    value: any,
  ) {
    while (td.firstChild) {
      td.removeChild(td.firstChild);
    }

    const tags: string[] = Array.isArray(value) ? value : [];
    const container = document.createElement("div");
    container.className = "em-tags-cell";

    if (tags.length === 0) {
      const placeholder = document.createElement("span");
      placeholder.className = "em-tags-placeholder";
      placeholder.textContent = "Adicionar tags";
      container.appendChild(placeholder);
      td.title = "";
    } else {
      tags.slice(0, MAX_VISIBLE).forEach((tag) => {
        const chip = document.createElement("span");
        chip.className = "em-tag-chip";
        chip.textContent = tag;
        container.appendChild(chip);
      });
      if (tags.length > MAX_VISIBLE) {
        const more = document.createElement("span");
        more.className = "em-tag-chip em-tag-more";
        more.textContent = `+${tags.length - MAX_VISIBLE}`;
        container.appendChild(more);
      }
      td.title = tags.join(", ");
    }

    td.appendChild(container);
  };
}

// Render de uma célula de cor: desenha um swatch arredondado com a cor (hex) da
// célula. A coluna usa editor: false e a edição acontece pelo popover de cor
// (copiar/colar e autofill continuam funcionando sobre a string hex). Em linhas
// de Texto no modo "html" as cores não se aplicam → célula desabilitada.
export function createColorRenderer() {
  return function colorRenderer(
    instance: any,
    td: any,
    row: any,
    _col: any,
    prop: any,
    value: any,
  ) {
    while (td.firstChild) {
      td.removeChild(td.firstChild);
    }

    const rowData = instance.getSourceDataAtRow(row) as any;
    const isDisabled = rowData?.mode === "html";

    td.style.textAlign = "center";
    td.style.verticalAlign = "middle";

    if (isDisabled) {
      td.classList.add("disabled-cell");
    } else {
      td.classList.remove("disabled-cell");
    }

    const container = document.createElement("div");
    container.style.display = "flex";
    container.style.alignItems = "center";
    container.style.justifyContent = "center";
    container.style.height = "100%";

    const color = typeof value === "string" ? value : "";

    if (color) {
      const swatch = document.createElement("div");
      swatch.className = "em-color-swatch";
      // A cor vai numa camada por cima do xadrez: cores sólidas o cobrem
      // (sem falsa opacidade); cores com alpha deixam o xadrez transparecer.
      const fill = document.createElement("div");
      fill.className = "em-color-swatch__fill";
      fill.style.backgroundColor = color;
      swatch.appendChild(fill);
      container.appendChild(swatch);
    } else if (!isDisabled) {
      const placeholder = document.createElement("span");
      placeholder.className = "em-color-placeholder";
      placeholder.textContent = "Definir cor";
      container.appendChild(placeholder);
    }

    td.appendChild(container);
    td.title = isDisabled ? "Disponível apenas no modo Texto" : color || "";
  };
}

// Render do icone selecionado (lê de um cache pré-computado)
export function createIconRenderer(iconCache: Map<string, string>) {
  return function iconRenderer(
    instance: any,
    td: any,
    row: any,
    col: any,
    prop: any,
    value: any,
  ) {
    while (td.firstChild) {
      td.removeChild(td.firstChild);
    }

    const src = value ? iconCache.get(value) : undefined;
    const container = document.createElement("div");
    container.style.display = "flex";
    container.style.alignItems = "center";
    container.style.justifyContent = "center";
    container.style.height = "100%";

    if (src) {
      const img = document.createElement("img");
      img.src = src;
      img.style.width = "20px";
      img.style.height = "20px";
      img.style.objectFit = "contain";
      container.appendChild(img);
    }

    td.appendChild(container);
    td.style.textAlign = "center";
    td.style.verticalAlign = "middle";

    // Aplica indicação visual de erro
    const rowData = instance.getSourceDataAtRow(row) as TableRow;
    const error = rowData?._errors?.[prop];
    const existingIcon = td.querySelector(".cell-error-icon");
    if (existingIcon) existingIcon.remove();

    if (error) {
      td.classList.add("htInvalid");
      td.title = error;
      const errIcon = document.createElement("span");
      errIcon.className = "cell-error-icon";
      errIcon.textContent = "!";
      td.appendChild(errIcon);
    } else {
      td.classList.remove("htInvalid");
      td.title = "";
    }
  };
}
